import { useMemo, useEffect, useState, useRef } from 'react'
import { Spacing, Heading, Accordion, Button, Afterpay } from '@big-red-group/storefront-common-ui'
import Breadcrumb from './Breadcrumb'
import Sustainability from './Sustainability'
import {
  MinimalHeader,
  SmallContainer,
  MobileBookNow,
  Currency,
  FromPrice,
  Price,
  PricingContainer,
  MinimalPricing,
} from './MinimalProductPage.styles'
import Location from './Location'
import NeedHelp from './NeedHelp'
import { CreateProductSessionShownEventInput, PdpResponse, Voucher } from '@/types'
import HotDealMessage from './HotDealMessage'
import { processMoreInfo } from '@/helpers/MoreInfoHelper'
import { BOOKING_ENGINE_CHECKOUT_STEPS, CHECKOUT_STEPS } from '@/constant/segment'
import { CheckoutStep } from '@/types'
import Stepper from './Stepper'
import ContentBlock from './ContentBlock'
import { getHotDeals } from '@/helpers/BookingHelper'
import BookingNew from '@/components/BookingNew'
import { scrollToElement } from '@/helpers/PageScrollHelper'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import { getRetailPrice } from '@/helpers/PriceHelper'
import { EDITING_FLAG } from '@/constant/segment'
import { UserBookingSelections } from '@/types'
import { useRouter } from 'next/router'
import { SelectedSessionWithVariant } from '@/types'
import {
  useProductRemoved,
  useProductSessionChecked,
  useProductSessionShown,
  useProductViewed,
} from '@/hooks/AnalyticsProductHook'
import { useProductAdded } from '@/hooks/AnalyticsProductHook'
import { useElementClick } from '@/hooks/HTMLElementClickHook'
import Head from 'next/head'
import { getIndexTag } from '@/helpers/SeoHelper'
import { renderOpenGraph } from '@/helpers/PageTitleHelper'
import Accessibility from './Accessibility'
import { useAnalyticsError } from '@/hooks/AnalyticsErrorHook'
import { logger } from '@/logging/Logger'
import { AccordionLabel } from './AccordionLabel.styles'
import { formatCurrency, useCart } from '@big-red-group/storefront-common-checkout'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { CartItemData } from '@big-red-group/storefront-common-checkout'
import { createCanonicalLink } from '@/helpers/UrlHelper'
import { StaticAvailability } from '@/types'

const log = logger()

const MinimalProductLayout: React.FC<
  Pick<
    PdpResponse,
    | 'productDetails'
    | 'productRecommendations'
    | 'productReviews'
    | 'moreToExplore'
    | 'agentConfiguration'
    | 'loyaltyCampaign'
    | 'lastMinutePricingDates'
  > & {
    canonicalLink?: string
    staticAvailability: StaticAvailability | {}
  }
> = ({ productDetails, canonicalLink, lastMinutePricingDates, staticAvailability }) => {
  const {
    id,
    operatorName,
    name,
    fromPrice,
    productSessions,
    highlights,
    helpCode,
    description,
    moreInformation,
    geolocations,
    bookingRequired,
    bookingNotesRequired,
    bookingNotesPlaceholder,
    canonicalRegionUrlSegment,
    urlSegment,
    regionId,
    operatorId,
    handlerKey: productHandlerKey,
    productSessionGroups,
    title,
    summary,
    decorators,
    cancellationTerm,
    images,
    accessibility,
    timezone,
  } = productDetails

  const needToKnowContent = processMoreInfo(moreInformation)

  const steps: CheckoutStep[] = useMemo(
    () =>
      BOOKING_ENGINE_CHECKOUT_STEPS.map((step, index) => ({
        label: step.label,
        icon: step.icon,
        active: index === 0,
        complete: false,
      })),
    []
  )

  const featuredHotDeal = getHotDeals(productSessions)
  const mobileStickyPos = useRef<HTMLDivElement>(null)
  const [scrolledPastPos, setScrolledPastPos] = useState(false)
  const defaultProperties = useDefaultProperties()
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent
  const partnerCode = defaultProperties?.agentConfiguration?.partnerCode
  const productLayout = defaultProperties?.agentConfiguration?.productLayout
  const callCentreNumber = defaultProperties?.agentConfiguration?.agentCallCentrePhoneNumber
  const displayAfterPay = defaultProperties?.agentConfiguration?.paymentOptions?.some((po) => po.type === 'AFTERPAY')!!
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  const retailPrice = getRetailPrice(productSessions)
  const isGiftCardProduct = productHandlerKey === 'exozGiftCard2'
  const [userBookingSelections, setUserBooking] = useState<UserBookingSelections>({})
  const [itemUuid, setItemUuid] = useState<string | undefined>(undefined)
  const { data: cart } = useCart('cart', baseUrl)
  const [editCartItem, setEditCartItem] = useState<CartItemData>()
  const router = useRouter()
  const trackProductAdded = useProductAdded()
  const trackProductSessionChecked = useProductSessionChecked(productDetails)
  const trackProductSessionShown = useProductSessionShown(productDetails)
  const trackButtonClicked = useElementClick('Button')
  const trackProductViewed = useProductViewed()
  const trackProductRemoved = useProductRemoved()
  const errorOccurred = useAnalyticsError()
  const canonicalGroupUrlSegment = productDetails?.canonicalGroupUrlSegment

  const { editing: editProductUUID } = router.query
  const isProductEdit = EDITING_FLAG in router.query ? true : false

  // TODO: OMS
  // useEffect(() => {
  //   const itemUUID = editProductUUID ? editProductUUID : ''
  //
  //   if (!itemUUID) return
  //   const editItem = cart?.items?.find((item) => item.uuid === itemUUID)
  //   if (editItem) {
  //     updateUserBooking({
  //       previouslySelectedOptions: loadPreviouslySelectedOptions(productSessions ?? [], editItem),
  //       previouslySelectedDate: new Date(editItem.bookingDate!),
  //       previouslySelectedSession: editItem.metadata?.sessionId,
  //       previouslySelectedPickup: editItem.pickup,
  //       previouslySelectedGroupId: loadPreviouslySelectedGroupId(editItem),
  //       isEdit: isProductEdit,
  //     })
  //     setItemUuid(editItem.uuid)
  //     setEditCartItem(editItem)
  //   }
  // }, [])

  useEffect(() => {
    trackProductViewed(productDetails, isProductEdit, productLayout, featuredHotDeal, decorators)
  }, [trackProductViewed, productDetails, isProductEdit, productLayout, ,])

  const updateUserBooking = (moreSelections: UserBookingSelections) => {
    setUserBooking({ ...userBookingSelections, ...moreSelections })
  }

  const renderPricing = (renderAfterpay: boolean) => {
    return (
      <>
        <MinimalPricing onSale={retailPrice != fromPrice}>
          <FromPrice>
            From {retailPrice && retailPrice != fromPrice && <span>{formatCurrency(retailPrice)}</span>}
          </FromPrice>
          <Price data-testid="product-price">{formatCurrency(fromPrice)}</Price>
          <Currency data-testid="total-price">{defaultProperties?.agentConfiguration?.currencyCode ?? 'AUD'}</Currency>
        </MinimalPricing>
        {renderAfterpay && <Afterpay amount={fromPrice} installment="4" />}
      </>
    )
  }

  const addProductToCart = (isGift: boolean, selectedVariants?: SelectedSessionWithVariant[], voucher?: Voucher) => {
    if (selectedVariants?.length) {
      for (const selectedVariant of selectedVariants) {
        const { option: variant, quantity, ...session } = selectedVariant

        trackProductAdded({
          product: productDetails,
          url: window.location.href,
          isGift,
          quantity,
          isProductEdit,
          session,
          variant,
          voucher,
          filteredDecorators: decorators,
        })
      }

      return
    }

    trackProductAdded({
      product: productDetails,
      url: window.location.href,
      isGift,
      quantity: 1,
      isProductEdit,
      voucher,
      filteredDecorators: decorators,
    })
  }

  // Trigger Product Session Checked event

  const sessionChecked = (
    sessionFrom: Date,
    sessionTo: Date,
    bookingCalendarType?: string,
    hasHotDeal?: boolean,
    hasLastMinutePricing?: boolean
  ) => {
    trackProductSessionChecked({
      sessionFrom,
      sessionTo,
      isProductEdit,
      bookingCalendarType,
      hasHotDeal,
      hasLastMinutePricing,
    })
  }

  // Trigger Product Session Shown event
  const sessionShown = (eventInput: Omit<CreateProductSessionShownEventInput, 'product' | 'url'>) => {
    eventInput = { ...eventInput, isProductEdit }
    trackProductSessionShown(eventInput)
  }
  useEffect(() => {
    if (window.innerWidth < 768) {
      window.addEventListener('scroll', () => {
        if (mobileStickyPos.current != null) {
          setScrolledPastPos(window.scrollY > mobileStickyPos.current.offsetTop)
        }
      })
    }
  }, [])

  const handleClickSelectTickets = () => {
    scrollToElement('booking')
  }

  const renderBookingComponent = () => {
    const bookingProps = {
      displayAfterpay: false,
      productSessionGroups,
      handlerKey: productHandlerKey,
      productId: id,
      productName: name,
      operatorName,
      operatorId,
      productImage: images && images[0],
      fromPrice,
      retailPrice,
      sessions: productSessions,
      bookingRequired,
      bookingNotesRequired: bookingNotesRequired!,
      bookingNotesPlaceholder: bookingNotesPlaceholder!,
      regionUrlSegment: canonicalRegionUrlSegment,
      productUrlSegment: urlSegment,
      regionId,
      onAddToCart: addProductToCart,
      onSessionChecked: sessionChecked,
      onSessionShown: sessionShown,
      userBookingSelections,
      onUserSelection: updateUserBooking,
      onBookNowClick: trackButtonClicked,
      uuid: itemUuid,
      lastMinutePricingDates,
      timezone: timezone!,
      staticAvailability,
    }

    return <BookingNew {...bookingProps} />
  }

  return (
    <div>
      <Head>
        <link
          rel="canonical"
          href={createCanonicalLink(`${canonicalRegionUrlSegment}/${canonicalGroupUrlSegment || urlSegment}`)}
        />
        <title>{title}</title>
        <meta name="description" property="og:description" content={summary} />
        {getIndexTag(isBrgAgent, 'pdp', partnerCode!)}
        {renderOpenGraph(title, summary, images && images[0])}
      </Head>
      <Stepper steps={steps} />
      <SmallContainer>
        <Breadcrumb items={[{ text: 'Back to all experiences', href: '/en' }]} />
        <Spacing size="M" />
        <MinimalHeader>
          <Heading size="XL" as="h1" spacing={false} dataTestId="product-name">
            {name}
          </Heading>
          <PricingContainer>{renderPricing(displayAfterPay)}</PricingContainer>
        </MinimalHeader>
        {featuredHotDeal && (
          <>
            <Spacing size="M" />
            <HotDealMessage message={featuredHotDeal} />
          </>
        )}
        <Spacing size="XXXL" />
        {!isGiftCardProduct && (
          <>
            {renderBookingComponent()}
            <Spacing size="XXXL" />
          </>
        )}
        <div ref={mobileStickyPos}></div>
        <Heading size="L" dataTestId="product-details">
          What you can expect
        </Heading>
        <ContentBlock content={`<p>${description}</p>`} style={'whiteSpace: pre-line'} />
        <Heading size="L">What you need to know</Heading>
        {needToKnowContent.map(({ heading, content }, index) => (
          <Accordion openOnLoad={index == 0} key={index} content={content} anchorColor="brand-secondary">
            <AccordionLabel>{heading}</AccordionLabel>
          </Accordion>
        ))}
        <Spacing size="XXXL" />
        <div mcp-id="product-details"></div>
        {geolocations && (
          <Location
            latitude={geolocations[0].latitude}
            longitude={geolocations[0].longitude}
            address={geolocations[0].address}
            dataTestId="product-location"
          />
        )}
        {callCentreNumber && (
          <NeedHelp
            callCentreNumber={callCentreNumber}
            helpCode={helpCode}
            supplierName={operatorName}
            showButtons={false}
            dataTestId="product-help"
          />
        )}
        <Spacing />
        {accessibility && (
          <>
            <Heading size="L" headingGraphic="stroke">
              Accessibility
            </Heading>
            <Accessibility content={accessibility} productId={id} />
            <Spacing size="XXL" />
          </>
        )}
        {isBrgAgent && <Sustainability />}
        <MobileBookNow active={scrolledPastPos} data-testid="mobile-book-bar">
          <Button size="L" style={{ width: '100%' }} onClick={() => handleClickSelectTickets()}>
            {`${isGiftCardProduct ? 'Select tickets' : 'Buy now'}`}
          </Button>
          {featuredHotDeal && <HotDealMessage message={featuredHotDeal} />}
        </MobileBookNow>
      </SmallContainer>
    </div>
  )
}

export default MinimalProductLayout
